import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import {
  FadeOverflow,
  hiddenScrollbarCss,
  InfoMessage,
  SearchLoupeIcon,
} from '@root/shared/ui-kit'

import type { Error } from '@root/shared/core'
import type { ChildrenProps, ClassNameProps } from '@root/shared/react'
import type { IconProps } from '@root/shared/ui-kit'

const THRESHOLD = 20

// TODO: Support virtualization
type TradingListProps<T> = {
  state: AsyncStateN.AsyncStateN<Error.UnknownError, Array<T>>
  getKey: (it: T) => string
  renderItem: (it: T) => React.ReactElement
  renderSkeleton: () => React.ReactElement
  skeletonsCount?: number
  emptyText?: React.ReactNode
  emptyTitle?: React.ReactNode
  search?: boolean
  onRetry?: () => void
  hasMore?: boolean
  onLoadMore?: () => void
  loadMoreState?: AsyncStateN.AsyncStateN<Error.UnknownError, unknown>
  emptyIcon?: React.ReactElement<IconProps>
} & ClassNameProps &
  ChildrenProps

export const TradingList = <T,>({
  renderItem,
  state,
  className,
  getKey,
  renderSkeleton,
  skeletonsCount = 10,
  emptyText,
  emptyTitle,
  search,
  onRetry,
  onLoadMore,
  loadMoreState,
  hasMore,
  emptyIcon,
}: TradingListProps<T>) => {
  const { t } = useTranslation()

  const skeletons = useMemo(
    () => Array.range(1, skeletonsCount),
    [skeletonsCount],
  )
  const ref = useRef<HTMLDivElement>(null)

  const handleScroll: React.UIEventHandler = (event) => {
    if (
      !loadMoreState ||
      AsyncStateN.isLoading(loadMoreState) ||
      AsyncStateN.isError(loadMoreState) ||
      !hasMore
    ) {
      return
    }

    const target = event.currentTarget
    if (target.clientHeight === target.scrollHeight) {
      return
    }

    const scrollBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight

    if (scrollBottom <= THRESHOLD) {
      onLoadMore?.()
    }
  }

  const renderContent = () => {
    if (AsyncStateN.isSuccess(state)) {
      const items = state.value
      if (items.length === 0) {
        const icon = React.cloneElement(emptyIcon ?? <SearchLoupeIcon />)

        if (search) {
          return (
            <EmptyContainer>
              <InfoMessage
                intent="empty"
                title={t('common.no-results')}
                description={emptyText || t('common.try-changing-search-query')}
                icon={icon}
              />
            </EmptyContainer>
          )
        }

        return (
          <EmptyContainer>
            <InfoMessage
              intent="empty"
              title={emptyTitle}
              description={t('common.try-changing-search-query')}
              icon={icon}
            />
          </EmptyContainer>
        )
      }

      const renderLoadMoreContent = () => {
        if (!loadMoreState) {
          return undefined
        }

        if (AsyncStateN.isError(loadMoreState)) {
          // TODO: rework
          return (
            <InfoContainer>
              <InfoMessage intent="error" onRetry={onLoadMore} />
            </InfoContainer>
          )
        }

        if (hasMore) {
          return (
            <>
              {renderSkeleton()}
              {renderSkeleton()}
            </>
          )
        }
      }

      return (
        <List
          onScroll={handleScroll}
          mode="always"
          variant="vertical"
          ref={ref}
        >
          {items.map((it) => (
            <React.Fragment key={getKey(it)}>{renderItem(it)}</React.Fragment>
          ))}
          {renderLoadMoreContent()}
        </List>
      )
    }

    if (AsyncStateN.isError(state)) {
      return (
        <EmptyContainer>
          <InfoMessage onRetry={onRetry} intent="error" />
        </EmptyContainer>
      )
    }

    return (
      <List mode="always" variant="vertical">
        {skeletons.map((it) => (
          <React.Fragment key={it}>{renderSkeleton()}</React.Fragment>
        ))}
      </List>
    )
  }

  return <Root className={className}>{renderContent()}</Root>
}

const Root = styled.div`
  display: grid;
  min-height: 0;
`

const EmptyContainer = styled.div`
  display: grid;
`

const List = styled(FadeOverflow)`
  display: grid;
  gap: 6px;
  padding-block: 8px var(--safe-bottom);
  padding-inline: var(--trading-list-padding-inline, 12px);
  overflow-y: auto;
  align-content: start;
  ${hiddenScrollbarCss}
`

const InfoContainer = styled.div`
  display: grid;
  padding-block: 16px 8px;
`
